import { useMutation } from '@tanstack/react-query';
import { useApolloClient } from '@apollo/client';
import steven from 'helpers/steven';
import {
  request,
  type V3ApiSuccessfulResponse,
  type V3ApiFailedResponse,
} from 'javascripts/services/v3ApiClient';
import { PageObject } from 'data/v3/data-contracts';
import * as immutable from 'object-path-immutable';
import { GET_PROJECT_PAGES } from 'components/projects/pages/gql/getProjectPages.graphql';
import { graphQLIdGenerator } from 'helpers/graphql-utils';
import { PageTemplateId } from '../types';

type CreatePageFromTemplateResponse = V3ApiSuccessfulResponse<PageObject>;

type CreatePageFromTemplateParams = {
  template_id: PageTemplateId;
};

export const useCreatePageFromTemplate = ({
  projectDbId,
  onSuccess,
  onError,
}: {
  projectDbId: number;
  onSuccess?: (data: PageObject) => void;
  onError?: () => void;
}) => {
  const client = useApolloClient();

  return useMutation<
    CreatePageFromTemplateResponse,
    V3ApiFailedResponse,
    CreatePageFromTemplateParams
  >({
    mutationFn: async (params) => {
      const { template_id } = params;

      return request({
        url: `projects/${projectDbId}/pages/from_template`,
        method: 'post',
        data: {
          template_id,
        },
      });
    },
    onSuccess: async ({ data }: { data: PageObject }) => {
      const projectNodeId = graphQLIdGenerator('Location', projectDbId);
      const variables = { projectNodeId };

      try {
        // Updating Apollo client cache with the new page
        // TODO: Update/remove apollo logic when pages is moved to react-query
        const cachedProjectPages = await client.readQuery({
          query: GET_PROJECT_PAGES,
          variables,
        });

        const newProjectPage = {
          ...data,
          id: graphQLIdGenerator('Page', data.id),
          databaseId: data.id,
          __typename: 'ProjectPage',
          lastEditedBy: {
            ...data.lastEditedBy,
            name: data.lastEditedBy.fullName,
          },
        };

        if (cachedProjectPages) {
          const updatedProjectPages = immutable.insert(
            cachedProjectPages,
            `viewer.project.pages.nodes`,
            newProjectPage,
            0,
          );

          await client.writeQuery({
            query: GET_PROJECT_PAGES,
            variables,
            data: updatedProjectPages,
          });
        }

        steven.entityCrud({
          entity_id: `${data.id}`,
          entity_type: 'page',
          action: 'created',
          source: 'template',
        });
      } catch (e) {
        onError?.();
      } finally {
        onSuccess(data);
      }
    },
    onError: () => {
      onError?.();
    },
  });
};
